import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { Dialog } from "components/dialog";
import { MdOutlineCloudUpload } from "react-icons/md";
import { Loader2, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { agentsApi } from "api/agents";
import { AgentImportWebSocketMessage, useAgentImportWebSocket } from "hooks/useAgentImportWebSocket";
import { formatFileSize, handleAxiosAndCaughtError } from "utils";

interface ImportAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess?: () => void;
}

const ImportAgentModal = ({
  isOpen,
  onClose,
  onImportSuccess,
}: ImportAgentModalProps) => {
  const { t } = useTranslation();
  const [importTaskId, setImportTaskId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [agentName, setAgentName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const disconnectRef = useRef<(() => void) | null>(null);

  // Reset all state to initial values
  const resetState = useCallback(() => {
    setSelectedFile(null);
    setAgentName("");
    setPassword("");
    setIsLoading(false);
    setImportTaskId(null);
    // Disconnect WebSocket if connected
    if (disconnectRef.current) {
      disconnectRef.current();
      disconnectRef.current = null;
    }
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  // Handle import status updates from websocket
  const handleImportStatusUpdate = useCallback(
    (message: AgentImportWebSocketMessage) => {
      if (message.status === "completed") {
        // Import completed successfully
        setIsLoading(false);
        // Disconnect WebSocket and close modal
        if (disconnectRef.current) {
          disconnectRef.current();
          disconnectRef.current = null;
        }
        setImportTaskId(null);
        onClose();
        onImportSuccess?.();
      } else if (message.status === "failed") {
        // Import failed
        setIsLoading(false);
        // Disconnect WebSocket on failure
        if (disconnectRef.current) {
          disconnectRef.current();
          disconnectRef.current = null;
        }
        setImportTaskId(null);
      } else if (
        message.status === "processing" ||
        message.status === "pending"
      ) {
        // Import in progress
        setIsLoading(true);
      }
    },
    [onClose, onImportSuccess, t]
  );

  // Setup websocket connection for import status
  const { disconnect } = useAgentImportWebSocket({
    taskId: importTaskId,
    onStatusUpdate: handleImportStatusUpdate,
    enabled: !!importTaskId,
  });

  // Store disconnect function for cleanup
  useEffect(() => {
    if (disconnect) {
      disconnectRef.current = disconnect;
    }
    return () => {
      if (disconnectRef.current) {
        disconnectRef.current();
        disconnectRef.current = null;
      }
    };
  }, [disconnect]);

  // Handle file upload
  const handleFileUpload = useCallback(
    async (file: File, name?: string, password?: string) => {
      setIsLoading(true);

      try {
        const response = await agentsApi.import(file, name, password);
        if ("task_id" in response && response.task_id) {
          setImportTaskId(response.task_id);
        } else if ("data" in response && response.data?.agent_id) {
          setIsLoading(false);
          onClose();
          onImportSuccess?.();
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error importing agent:", err);
        setIsLoading(false);
        handleAxiosAndCaughtError(err);
      }
    },
    [t, onClose, onImportSuccess]
  );

  // Setup dropzone
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
    },
    maxFiles: 1,
    disabled: isLoading,
    onDropRejected: (fileRejections) => {
      const rejection = fileRejections[0];
      if (rejection?.errors?.[0]?.code === "file-invalid-type") {
        toast.error(t("agents.import.invalidFileType"));
      } else {
        toast.error(
          rejection?.errors?.[0]?.message || t("agents.import.invalidFileType")
        );
      }
    },
  });

  // Handle modal close
  const handleModalClose = useCallback(() => {
    if (!isLoading) {
      // Disconnect WebSocket if connected
      if (disconnectRef.current) {
        disconnectRef.current();
        disconnectRef.current = null;
      }
      resetState();
      onClose();
    }
  }, [isLoading, onClose, resetState]);

  // Handle import button click
  const handleImportClick = useCallback(() => {
    if (!selectedFile) {
      toast.error(t("agents.import.noFileSelected"));
      return;
    }
    if (!agentName || agentName.trim() === "") {
      toast.error(t("agents.import.agentNameRequired"));
      return;
    }
    handleFileUpload(selectedFile, agentName, password || undefined);
  }, [selectedFile, agentName, password, handleFileUpload, t]);

  return (
    <Dialog
      isOpen={isOpen}
      setIsOpen={handleModalClose}
      onClose={handleModalClose}
      header={t("agents.import.title")}
      subHeader={t("agents.import.subtitle")}
      className="w-1/3"
      backdropHandlerDisabled={isLoading}
      outsideClickDisabled={isLoading}
    >
      <div className="flex flex-col gap-4">
        {/* Dropzone */}
        {!selectedFile && (
          <div className="flex flex-col gap-4">
            <div className="flex-1">
              <div
                {...getRootProps()}
                className={`flex w-full cursor-pointer flex-col items-center justify-center rounded-xl border-[1px] border-dashed border-gray-200 bg-gray-100 py-5 dark:border-gray-700 dark:bg-navy-700 ${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                }`}
              >
                <input {...getInputProps()} disabled={isLoading} />
                <MdOutlineCloudUpload className="mb-2 text-[30px] text-brand-500 dark:text-white" />
                <div className="text-center">
                  <p className="text-base font-semibold text-navy-700 dark:text-white">
                    {t("agents.import.dragDrop")}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t("agents.import.clickOrDrag")}
                  </p>
                </div>
                <button
                  type="button"
                  className="mt-3 rounded border border-brand-500 bg-white px-4 py-1 text-xs font-semibold text-brand-500 transition hover:bg-brand-50 dark:border-brand-400 dark:bg-navy-800 dark:text-brand-400 dark:hover:bg-navy-700"
                  disabled={isLoading}
                >
                  {t("agents.import.browse")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Selected File List */}
        {selectedFile && (
          <div className="flex flex-col gap-4">
            <div className="max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-navy-700">
              <div className="flex items-center justify-between border-b border-gray-100 py-2 last:border-b-0 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-brand-500 dark:bg-navy-800 dark:text-brand-400">
                    <MdOutlineCloudUpload />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-navy-700 dark:text-white">
                      {selectedFile.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(selectedFile.size)}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="text-gray-400 transition hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                  title={t("common.remove")}
                  onClick={() => setSelectedFile(null)}
                  disabled={isLoading}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Agent Name Input */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-navy-700 dark:text-white">
                {t("agents.import.agentName")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder={t("agents.import.agentNamePlaceholder")}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-700 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500 dark:focus:border-brand-400"
                disabled={isLoading}
                required
              />
            </div>

            {/* Password Input */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-navy-700 dark:text-white">
                {t("agents.import.password")}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("agents.import.passwordPlaceholder")}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-700 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500 dark:focus:border-brand-400"
                disabled={isLoading}
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-auto flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-navy-600"
            onClick={handleModalClose}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleImportClick}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-500"
            disabled={
              !selectedFile ||
              !agentName ||
              agentName.trim() === "" ||
              isLoading
            }
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                {t("agents.import.uploading")}
              </span>
            ) : (
              t("agents.import.button")
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ImportAgentModal;
